import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, FileCheck2, Plus } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { ApiError } from "@/lib/api";
import { checkFile, uploadAttachment } from "@/lib/attachment-upload";
import { todayIso } from "@/lib/date-range";
import { formatDateIn } from "@/lib/datetime";
import {
  employeeApi,
  type EmployeeConfig,
  type EmployeeRequest,
  type RequestStatus,
  type RequestType,
} from "@/lib/employee-api";
import {
  displayTypeName,
  requestStatusLabel,
  requestStatusVariant,
  webRequestTypes,
} from "@/lib/request-labels";

/**
 * The employee's own requests — apply, track, withdraw.
 *
 * Without this the web lane was telling people to do something they had no way
 * of doing: the missed-attendance notification says "if you worked, submit a
 * correction request", and Work-From-Anywhere (BQ B4) is an
 * apply-then-approve workflow whose apply step existed only on mobile.
 */

/** Only these two may still be withdrawn (BR-032); the rest are terminal. */
const isWithdrawable = (s: RequestStatus) =>
  s === "PENDING" || s === "NEEDS_CLARIFICATION";

export default function MyRequests() {
  const { toast } = useToast();
  const [config, setConfig] = useState<EmployeeConfig | null>(null);
  const [items, setItems] = useState<EmployeeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  // Remounts the file input so "Remove" really empties it.
  const [fileInputKey, setFileInputKey] = useState(0);

  const tz = config?.tenant.timezone ?? "Asia/Kuala_Lumpur";

  const [form, setForm] = useState({
    type: "LEAVE" as RequestType,
    date: "",
    dateTo: "",
    requestedChange: "",
    reason: "",
  });

  /**
   * The tenant's request types (FEAT-2026-031), or the built-in list from an
   * older API. Each carries its name, its dates (the server drops `dateTo` for
   * a single-day type, BR-031, so only a range type asks for one) and its
   * attachment rule. Work from anywhere needs the tenant's WFA (BR-161).
   */
  const types = useMemo(() => webRequestTypes(config), [config]);
  const chosen = types.find((o) => o.value === form.type) ?? types[0];

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cfg, res] = await Promise.all([
        employeeApi.config(),
        employeeApi.requests({ pageSize: 50 }),
      ]);
      setConfig(cfg);
      setItems(res.data);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load your requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const startNew = () => {
    const today = todayIso(config?.tenant.timezone);
    setForm({
      type: types[0]?.value ?? "LEAVE",
      date: today,
      dateTo: "",
      requestedChange: "",
      reason: "",
    });
    setFile(null);
    setFileError(null);
    setOpen(true);
  };

  const chooseFile = (chosen: File | null) => {
    const problem = chosen ? checkFile(chosen) : null;
    setFileError(problem);
    setFile(problem ? null : chosen);
  };

  const removeFile = () => {
    setFile(null);
    setFileError(null);
    setFileInputKey((k) => k + 1);
  };

  const submit = async () => {
    if (!form.date) {
      toast({ variant: "destructive", title: "Pick a date" });
      return;
    }
    // Mirrors the server's own minimums so a round trip isn't spent on a
    // message the form could have given immediately.
    if (form.requestedChange.trim().length < 5 || form.reason.trim().length < 5) {
      toast({
        variant: "destructive",
        title: "Add a little more detail",
        description: "Both what you're asking for and why need at least a few words.",
      });
      return;
    }
    const ranged = chosen?.dateMode === "range";
    if (ranged && form.dateTo && form.dateTo < form.date) {
      toast({ variant: "destructive", title: "The end date is before the start date" });
      return;
    }

    setSubmitting(true);
    try {
      // Upload first: the request carries the file's server-issued key.
      const attachments = file ? [await uploadAttachment(file)] : undefined;
      await employeeApi.createRequest({
        type: form.type,
        date: form.date,
        dateTo: ranged && form.dateTo ? form.dateTo : undefined,
        requestedChange: form.requestedChange.trim(),
        reason: form.reason.trim(),
        attachments,
      });
      setOpen(false);
      await load();
      toast({ title: "Request submitted", description: "Your approver has been notified." });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Could not submit your request",
        // ATTACHMENT_REQUIRED (BR-210) carries the tenant's own instruction, so
        // the server's message is shown as it is.
        description:
          e instanceof ApiError
            ? e.message
            : e instanceof Error && e.message === "Upload failed"
              ? "The attachment didn't upload. Try again."
              : "Something went wrong",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (id: string) => {
    try {
      await employeeApi.cancelRequest(id);
      await load();
      toast({ title: "Request withdrawn" });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Could not withdraw it",
        description: e instanceof ApiError ? e.message : "Something went wrong",
      });
    }
  };

  if (loading) return <Skeleton className="h-64 w-full" />;

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Couldn't load your requests</AlertTitle>
        <AlertDescription className="flex items-center gap-3">
          {error}
          <Button size="sm" variant="outline" onClick={() => void load()}>
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const ranged = chosen?.dateMode === "range";
  // BR-210: the tenant may require a file on this type, with its own words.
  // The tenant's attachment rule for the chosen type (BR-210).
  const rule = chosen?.attachment.required ? { instruction: chosen.attachment.instruction } : null;
  const canSubmit = !submitting && !fileError && (!rule || !!file);

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={startNew}>
          <Plus className="mr-1.5 h-4 w-4" />
          New request
        </Button>
      </div>

      <Card>
        <CardContent className="divide-y p-0">
          {items.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <FileCheck2 className="h-6 w-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                You haven't submitted any requests.
              </p>
            </div>
          )}
          {items.map((r) => (
            <div key={r.id} className="px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">{displayTypeName(r.type, r.typeLabel)}</span>
                    <Badge variant={requestStatusVariant(r.status)}>{requestStatusLabel(r.status)}</Badge>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {formatDateIn(r.date, tz)}
                    {r.dateTo && r.dateTo !== r.date && ` → ${formatDateIn(r.dateTo, tz)}`}
                  </p>
                  <p className="mt-1 text-xs">{r.requestedChange}</p>
                  {r.decidedByName && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {`Decided by ${r.decidedByName} (${r.decidedByRole === "LINE_MANAGER" ? "line manager" : "HR"})`}
                    </p>
                  )}
                  {r.approvalNotes && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {`Note: ${r.approvalNotes}`}
                    </p>
                  )}
                </div>
                {isWithdrawable(r.status) && (
                  <Button size="sm" variant="ghost" onClick={() => void cancel(r.id)}>
                    Withdraw
                  </Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New request</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <Field label="Type">
              <Select
                value={form.type}
                onValueChange={(v) => setForm((f) => ({ ...f, type: v as RequestType }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {types.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {chosen?.description && <p className="-mt-1 text-xs text-muted-foreground">{chosen.description}</p>}

            <div className="grid grid-cols-2 gap-3">
              <Field label={ranged ? "From" : "Date"}>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                />
              </Field>
              {ranged && (
                <Field label="To" hint="Leave blank for a single day">
                  <Input
                    type="date"
                    min={form.date || undefined}
                    value={form.dateTo}
                    onChange={(e) => setForm((f) => ({ ...f, dateTo: e.target.value }))}
                  />
                </Field>
              )}
            </div>

            <Field label="What are you asking for?">
              <Textarea
                rows={2}
                value={form.requestedChange}
                placeholder={
                  form.type === "WORK_FROM_ANYWHERE"
                    ? "e.g. Work from a client site in Penang"
                    : "e.g. Annual leave for a family event"
                }
                onChange={(e) =>
                  setForm((f) => ({ ...f, requestedChange: e.target.value }))
                }
              />
            </Field>

            <Field label="Reason">
              <Textarea
                rows={2}
                value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              />
            </Field>

            <div className="space-y-1.5">
              <Label htmlFor="request-attachment">
                {rule ? (
                  <>
                    Attachment
                    <span className="text-destructive"> *</span>
                  </>
                ) : (
                  "Attachment (optional)"
                )}
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  key={fileInputKey}
                  id="request-attachment"
                  type="file"
                  accept="image/jpeg,image/png,image/heic,application/pdf"
                  onChange={(e) => chooseFile(e.target.files?.[0] ?? null)}
                />
                {(file || fileError) && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label="Remove attachment"
                    onClick={removeFile}
                  >
                    Remove
                  </Button>
                )}
              </div>
              {rule?.instruction && (
                <p className="rounded-md bg-primary/5 p-2 text-xs">{rule.instruction}</p>
              )}
              <p className="text-xs text-muted-foreground">
                {rule && !rule.instruction
                  ? "Required — photo or PDF, up to 10 MB"
                  : "Photo or PDF, up to 10 MB"}
              </p>
              {fileError && <p className="text-xs text-destructive">{fileError}</p>}
            </div>

            {form.type === "WORK_FROM_ANYWHERE" && (
              <p className="text-xs text-muted-foreground">
                Once approved, you can check in from any location on these dates.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={() => void submit()} disabled={!canSubmit}>
              {submitting ? "Submitting…" : "Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
